import clsx from 'clsx';
import React, { useMemo } from 'react';

import { useAppContext } from '../../../../contexts/AppContext';
import {
  useCurrentUser,
  useEmailSettingsUpdate,
  useSubscribeToNewsletter,
  useUnsubscribeFromNewsletter,
} from '../../../../hooks';
import { subscribe_path, upgrade_subscription_path } from '../../../../routes';
import { DEFAULT_PRO_OFFER_CODE } from '../../../../src/constants';
import { getImgxSrc } from '../../../../utils/images';
import styles from './styles.module.scss';

interface EmailSettingOptionProps {
  accessLevel: number;
  author?: string;
  frequency?: string;
  newsletterId?: number;
  offerCode?: string;
  isPro?: boolean;
  isSubscribed: boolean;
  slug: string;
  subheader: string;
  title: string;
}

const EmailSettingsOption = ({
  accessLevel,
  author,
  frequency,
  newsletterId,
  offerCode,
  isPro,
  isSubscribed,
  slug,
  subheader,
  title,
}: EmailSettingOptionProps) => {
  const { currentUser } = useCurrentUser();
  const { mutate: subscribeToNewsletter } = useSubscribeToNewsletter(true);
  const { mutate: unsubscribeFromNewsletter } = useUnsubscribeFromNewsletter(true);
  const { mutate: updateEmailSettings } = useEmailSettingsUpdate();
  const { env } = useAppContext();

  const actionJSX = useMemo(() => {
    if (isPro && !currentUser.is_pro_subscriber) {
      return !currentUser.is_subscriber ? (
        <a href={subscribe_path({ offer: DEFAULT_PRO_OFFER_CODE })}>Sign up now</a>
      ) : (
        <a href={upgrade_subscription_path()}>Upgrade to Pro</a>
      );
    }

    if (currentUser.access_level < accessLevel) {
      return <a href={subscribe_path({ offer: offerCode })}>Sign up for access</a>;
    }

    if (isSubscribed) {
      return (
        <div
          className={styles.actionWrapper}
          onClick={() =>
            newsletterId
              ? unsubscribeFromNewsletter({ newsletter_id: newsletterId })
              : updateEmailSettings({ user: { article_notifications: 'topics' } })
          }
        >
          <img
            src={getImgxSrc({
              path: 'icons/checkbox_green.svg',
              isDevDependence: true,
              envContext: env,
            })}
            alt="Subscribed"
          />
          <div className={clsx(styles.actionLabel, styles.following)}>Following</div>
        </div>
      );
    } else {
      return (
        <div
          className={styles.actionWrapper}
          onClick={() =>
            newsletterId
              ? subscribeToNewsletter({ newsletter_id: newsletterId })
              : updateEmailSettings({ user: { article_notifications: 'all' } })
          }
        >
          <img
            src={getImgxSrc({
              path: 'icons/small_subscribe.svg',
              isDevDependence: true,
              envContext: env,
            })}
            alt="Subscribed"
          />
          <div className={styles.actionLabel}>Subscribe</div>
        </div>
      );
    }
  }, [
    currentUser,
    env,
    isPro,
    isSubscribed,
    accessLevel,
    offerCode,
    newsletterId,
    subscribeToNewsletter,
    unsubscribeFromNewsletter,
    updateEmailSettings,
  ]);

  return (
    <div
      className={clsx(styles.optionContainer, { [styles.articleContainer]: !newsletterId })}
      id={slug}
    >
      <div className={styles.optionWrapper}>
        <div className={styles.frequency}>{frequency}</div>
        <div className={styles.title}>{title}</div>
        {author && author != 'The Information Staff' && (
          <div className={styles.byLine}>by {author}</div>
        )}
        <div className={styles.subheader}>{subheader}</div>
        <div className={clsx(styles.actionContainer, 'action')}>{actionJSX}</div>
      </div>
    </div>
  );
};

export default EmailSettingsOption;
